/*
 * @Description: 启动编排：大区列表 → 敲定国家与语言 → 启动 i18n，并行兑换 SSO ticket，全部先于 React 挂载
 * @Author: liushichuan
 */
import config from '@/config';
import { bootI18n } from '@/i18n';
import commonService from '@/services/common';
import { IConvertedRegionItem } from '@/services/common/types';
import userService from '@/services/user';
import { ICountry } from '@/types/common';
import { getQueryParams, stripCredentialParams } from '@/utils/common';
import { isSupportedLanguage, resolveBootCountry } from '@/utils/countryUrlParams';
import { getLocalCountryInfo, removeRefreshToken, removeToken, setLocalCountryInfo, setLocalLanguage, setRefreshToken, setToken } from '@/utils/localStorage';

let cached: { language: string; promise: Promise<IConvertedRegionItem[]> } | null = null;

/**
 * 大区及国家列表：按语言缓存的模块级 promise，启动编排与 App 共享同一次请求。
 * 失败返回空数组而不是抛出——调用方据此维持现状，不会把用户选的国家冲掉。
 */
export const fetchRegionCountryList = (language: string) => {
  if (cached?.language !== language) {
    cached = {
      language,
      promise: commonService
        .getRegionCountryList(true)
        .then((list) => list || [])
        .catch((err) => {
          console.error('Failed to load regionCountryList:', err);
          return [] as IConvertedRegionItem[];
        }),
    };
  }
  return cached.promise;
};

let ssoTicketResult: 'success' | 'failed' | undefined;

/**
 * 取走启动阶段的 ticket 兑换结果：落地页匹配到路由时由 Init 取，匹配不到时由 404 页取（失败则转去登录）。
 * 只能取一次：Init 会随路由重建重新挂载，之后按普通已登录态走。
 * 失败提示留给它们弹——message 依赖 antd App context，启动阶段弹不出来。
 */
export const takeSsoTicketResult = () => {
  const result = ssoTicketResult;
  ssoTicketResult = undefined;
  return result;
};

/**
 * AlphaCloud ticket 免登：兑换 users-center 会话后，复用 authorize 换取本系统 token。
 *
 * 必须赶在 App 拉菜单之前：菜单按登录态下发，仅登录可见的页面（如 /online_academy）在游客菜单里
 * 没有路由，会落到顶层 404；而 404 在 mainLayoutRoute 之外，Init 不挂载，ticket 永远不会被兑换。
 */
const consumeSsoTicket = async () => {
  const ticket = getQueryParams('ticket');
  // 官网跳转优先级最高，Init 会直接清空登录态，ticket 不必兑换
  if (!ticket || getQueryParams('sourceType') === 'official') return;
  // endPoint 缺失时回退构建期配置的 AlphaCloud API 基址（同 Alpha Connect 的 apiPrefix 兜底）
  const endPoint = getQueryParams('endPoint') || import.meta.env.VITE_ALPHACLOUD_API_URL;
  // ticket 一次性，先从 URL 清除，避免刷新重复消费；此时 router 尚未创建，直接改 history 即可
  const { pathname, search, hash } = window.location;
  window.history.replaceState(window.history.state, '', `${pathname}${stripCredentialParams(search)}${hash}`);
  try {
    if (!endPoint) throw new Error('missing endPoint');
    const session = await userService.exchangeSsoTicket(endPoint, ticket);
    if (!session?.accessToken) throw new Error('missing accessToken');
    setToken(session.accessToken);
    const auth = await userService.authorize({ client_id: '100005', grant_type: 'client_credentials', access_token: session.accessToken });
    if (!auth?.access_token || !auth?.refresh_token) throw new Error('missing access_token / refresh_token');
    setToken(auth.access_token);
    setRefreshToken(auth.refresh_token);
    ssoTicketResult = 'success';
  } catch (err) {
    console.error('Failed to exchange SSO ticket:', err);
    // 清掉兑换中途写入的 token，免得菜单等后续请求带着无效 token 发出去
    removeToken();
    removeRefreshToken();
    ssoTicketResult = 'failed';
  }
};

/**
 * 启动编排，返回最终生效的国家。
 *
 * 必须在 ReactDOM.render 之前跑完：hooks/useLanguage、useAntdLocale 内部调用
 * react-i18next 的 useTranslation，而 App 顶部就用了它们。i18n 未 init 时 useTranslation 会
 * 提前返回、少调用一个 useState，init 完成后 hook 数量对不上，React 抛 "Should have a queue"
 * 整个应用崩掉。所以 React 挂载前 i18n 必须已经 init 完。
 *
 * 顺序上大区列表也必须最前置：国家是否开通、该国支持哪些语言只有它说了算，
 * 语言没定就启动 i18n 会先拉一份暂定语言的翻译、校正后再拉第二份。
 * 全程 catch 到底——启动失败也要让应用渲染出来，不能白屏；
 * 大区列表拿不到时按站点默认 Global / en-US 起步（见下方）。
 */
/**
 * 国家与语言分存两个 key：countryInfo 是启动取语言的来源，language 是请求头的来源，
 * 必须成对写。只修一个会出现「界面和请求头都是 en-US、但 countryInfo.language 是脏值」，
 * 国家选择面板按 countryData.language 比对 active，结果一个都不高亮。
 */
const persistCountry = (country: ICountry) => {
  setLocalCountryInfo(country);
  setLocalLanguage(country.language);
};

export const bootApp = async (): Promise<ICountry> => {
  const local = getLocalCountryInfo();
  // 本地缓存的语言可能是历史遗留 / 被手改的非法值，先落回默认；这一步要赶在第一个请求之前，
  // 否则 regionList 会带着脏值当 language 请求头发出去
  let country = isSupportedLanguage(local.language) ? local : { ...local, language: config.defaultLanguage };
  persistCountry(country);
  // ticket 仅 60s TTL，与大区列表并行兑换，不排到启动链路末尾
  const ssoTask = consumeSsoTicket();

  try {
    const regionCountryList = await fetchRegionCountryList(country.language);
    // 大区列表是国家取值的唯一权威，拿不到就无从校验，整体按站点默认 Global / en-US 起步，
    // 而不是带着未校验的国家往下跑（那会拿到空菜单，菜单里的页面一律 404）
    const resolved = regionCountryList.length ? resolveBootCountry(country, regionCountryList) : { ...config.defaultCountryInfo };
    if (resolved.countryCode !== country.countryCode || resolved.language !== country.language) {
      country = resolved;
      persistCountry(country);
    }
  } catch (err) {
    console.error('Failed to resolve boot country:', err);
    country = { ...config.defaultCountryInfo };
    persistCountry(country);
  }

  await bootI18n(country.language).catch((err) => {
    console.error('Failed to init i18n:', err);
  });
  await ssoTask;
  return country;
};
